import type { GuideChapter } from '../types'
import { n } from '../types'

export const admin: GuideChapter = {
  id: 'administration',
  title: 'Administration',
  icon: '🛠️',
  intro: `Brugere og roller, procedurer, TV-skærme og opsætningen af systemet. Findes under "Administration" i menuen - på telefonen under "Mere".`,
  sections: [
    {
      id: 'users',
      title: 'Brugere, roller og invitationer',
      icon: '👥',
      role: 'Administrator',
      where: `Administration → "Brugere"`,
      intro: `Se hvem der aldrig har været logget ind og send dem en invitation på SMS, opret lokale brugere, og giv roller.`,
      sketch: {
        device: 'desktop',
        title: 'Brugere',
        blocks: [
          {
            t: 'card',
            title: 'Kom i gang',
            children: [
              { t: 'row', text: '☐ Mikkel Hansen', meta: 'Aldrig logget ind', right: 'Sidst inviteret -', n: 1 },
              { t: 'btns', items: [{ text: 'Vælg alle, der mangler (4)' }, { text: 'Send invitation til 1 valgte', primary: true, n: 2 }] }
            ]
          },
          {
            t: 'card',
            title: 'Brugere (14)',
            children: [
              { t: 'btn', text: 'Opret lokal bruger', n: 3 },
              { t: 'row', text: 'Jens Montør · jens@tc-el.dk', meta: 'Microsoft 365', right: 'Aktiv' },
              { t: 'btns', items: [{ text: 'Superadmin' }, { text: 'Administrator' }, { text: 'Kontor' }, { text: 'Projektleder', n: 4 }, { text: 'Medarbejder', primary: true }, { text: 'Lærling' }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Invitér dem, der ikke er kommet i gang',
          steps: [
            n(1, `Sæt flueben i "Kom i gang", eller tryk "Vælg alle, der mangler".`),
            `Læs, hvordan SMS'en lyder.`,
            n(2, `Tryk "Send invitation til N valgte".`)
          ]
        },
        {
          title: 'Opret en lokal bruger (uden Microsoft 365)',
          steps: [
            n(3, `Tryk "Opret lokal bruger".`),
            `Udfyld navn, e-mail, midlertidig adgangskode og rolle, og tryk "Opret bruger".`,
            `Giv personen adgangskoden. Den skal skiftes ved første login.`
          ]
        },
        { title: 'Skift rolle', steps: [n(4, `Tryk på rolleknappen i rækken. En fremhævet knap betyder, at brugeren har rollen.`)] },
        {
          title: 'Kontor og Lærling',
          steps: [
            `Kontor kan alt, hvad en projektleder kan, og styrer desuden intranettet: opslag, håndbog, arbejdsgrupper, genveje og alle begivenheder. Kontoret får også advarslerne om udløbne certifikater, kalibrering, eftersyn og garantier. Kontoret kan ikke styre brugere, statusser, moduler eller indstillinger.`,
            `Lærling kan det samme som en medarbejder, men får ikke VPN-adgang.`,
            `Roller lægges sammen: gør du en konto til Lærling, så fjern "Medarbejder", ellers får kontoen stadig VPN.`
          ]
        },
        { title: 'Postkasser og mødelokaler', steps: [`Sæt flueben ved kontiene, og tryk "Skjul N valgte". Så står de ikke i kalender og lister.`] }
      ],
      tips: [
        `Invitationen kræver et telefonnummer fra Microsoft 365, og at SMS er slået til.`,
        `Kun en Superadmin kan give rollen Superadmin og ændre rollerne på en Superadmin - de knapper er grå for andre.`,
        `Du kan ikke deaktivere eller skjule dig selv.`
      ]
    },
    {
      id: 'users-superadmin',
      title: 'Slet brugere og nulstil to-faktor',
      icon: '🧹',
      role: 'Superadmin',
      where: `Administration → "Brugere"`,
      intro: `En Superadmin kan slette brugere, der aldrig har lavet noget, og nulstille to-faktor for en lokal bruger, der har mistet telefonen og koderne.`,
      flows: [
        { title: 'Slet', steps: [`Sæt flueben ved brugerne, tryk "Slet", og bekræft.`] },
        { title: 'Nulstil to-faktor', steps: [`Tryk "Nulstil to-faktor" i brugerens række. Brugeren sætter det op igen ved næste login.`] }
      ],
      tips: [`En bruger med spor - en opgave, en kommentar, en dag i kalenderen - springes over med en forklaring. Skjul brugeren i stedet.`]
    },
    {
      id: 'templates',
      title: 'Procedurer - skabeloner til sager',
      icon: '📋',
      // Projektleder og opefter retter procedurer og skabeloner (beslutning 141).
      role: 'Projektleder',
      where: `Administration → "Procedurer"`,
      intro: `En procedure er en type sag, f.eks. "Tavle". Hver procedure har en eller flere skabeloner med hovedemner og underemner. Underemnerne tæller i procent og giver 100 % tilsammen - det er dem, sagens fremdrift regnes ud fra.`,
      sketch: {
        device: 'desktop',
        title: 'Procedurer',
        blocks: [
          {
            t: 'card',
            title: 'Rediger Ny tavle i parcelhus',
            children: [
              { t: 'field', label: 'Procedure *', value: 'Tavle', n: 1 },
              { t: 'field', label: 'Navn *', value: 'Ny tavle i parcelhus' },
              { t: 'row', text: '1. Tavle', meta: 'Hovedemne', right: '40 %', n: 2 },
              { t: 'row', text: 'Opsætning', meta: 'Underemne', right: '25 %', n: 3 },
              { t: 'row', text: 'Tilslutning', meta: 'Underemne', right: '15 %' },
              { t: 'row', text: '2. Afslutning', meta: 'Hovedemne', right: '60 %' },
              { t: 'row', text: 'Test og dokumentation', meta: 'Underemne', right: '60 %' },
              { t: 'row', text: 'I alt 100 %', right: 'Fordel ligeligt', n: 4 },
              { t: 'btns', items: [{ text: '+ Tilføj hovedemne' }, { text: 'Gem skabelon', primary: true, n: 5 }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Opret en procedure',
          steps: [
            `Skriv navnet i feltet under "Procedurer", f.eks. "Tavle", og tryk "+ Opret procedure".`,
            `"Omdøb", "Skjul" og "Slet" står ved hver procedure. En procedure med skabeloner kan ikke slettes.`
          ]
        },
        {
          title: 'Lav en skabelon',
          steps: [
            `Tryk "Ny skabelon".`,
            n(1, `Vælg proceduren, og skriv skabelonens navn.`),
            n(2, `Skriv det første hovedemne. Flere hovedemner med "+ Tilføj hovedemne".`),
            n(3, `Skriv underemnerne under hvert hovedemne med "+ Tilføj underemne", og giv hvert en procent. Sæt evt. fast ansvarlig og dag efter start.`),
            n(4, `Følg "I alt" - den skal stå på 100 %. "Fordel ligeligt" deler 100 % ud på alle underemner.`),
            n(5, `Tryk "Gem skabelon". Giver underemnerne ikke 100 %, bliver den ikke gemt.`)
          ]
        },
        { title: 'En variant', steps: [`Tryk "Kopiér" og derefter "Rediger" på kopien.`] }
      ],
      tips: [
        `Ændringer påvirker ikke sager, der allerede er oprettet.`,
        `Tag en skabelon ud af brug ved at sætte "Status" til "Skjult" frem for at slette.`,
        `"Fast ansvarlig" kan være en person eller et team (👷). Med et team ser alle på holdet opgaven, og den første, der trykker "Tag opgaven", bliver ansvarlig.`,
        `Skabeloner fra før procedurerne står under "Uden procedure". Tryk "Rediger", vælg en procedure, del underemnerne op i hovedemner og få procenterne til at give 100 %.`
      ]
    },
    {
      id: 'teams-statuses',
      title: 'Teams, statusser og prioriteter',
      icon: '🎨',
      role: 'Administrator',
      where: `Administration → "Teams" og "Statusser"`,
      intro: `Teams samler folk på et hold. Statusser og prioriteter har navn og farve, som også ses på TV'et.`,
      flows: [
        { title: 'Nyt team', steps: [`Tryk "Nyt team", skriv navn, sæt flueben ved holdet, og tryk "Gem".`] },
        { title: 'Ret en status', steps: [`Ret navnet og klik ud af feltet, eller vælg en ny farve. Det gemmes med det samme.`] }
      ],
      tips: [
        `Ved hvert team står profilbillederne af dem, der er på holdet.`,
        `Et team styrer ikke rettigheder - det gør rollen. Ligger en opgave hos teamet, kan holdet dog se den sag, opgaven hører til. Statusser med systemkode kan ikke slettes.`
      ]
    },
    {
      id: 'screens',
      title: 'TV-skærme',
      icon: '📺',
      role: 'Administrator',
      module: 'screens',
      where: `Administration → "TV-skærme"`,
      intro: `En ny skærm er en Raspberry Pi med systemets image. Den viser en kode på TV'et, og du taster koden her.`,
      sketch: {
        device: 'tv',
        title: 'Tilknyt skærmen',
        caption: `TV'et, når en ny Pi er sat til`,
        blocks: [
          { t: 'title', text: 'theodor christiansen' },
          { t: 'code', text: '482 915', n: 1 },
          { t: 'text', text: 'Gå til TV-skærme i adminpanelet, tryk Tilknyt med kode, og skriv koden.' }
        ]
      },
      flows: [
        {
          title: 'Sæt en ny skærm op',
          steps: [
            `Tryk "Hent image", og skriv det på et SD-kort med Raspberry Pi Imager. Brug evt. "Lav Wi-Fi-fil".`,
            `Sæt kortet i Pi'en, forbind den til TV'et, og sæt strøm til.`,
            n(1, `TV'et viser en kode på seks cifre.`),
            `Tryk "Tilknyt med kode", skriv koden, navn og dashboard, og tryk "Tilknyt".`
          ]
        },
        { title: 'Tænd og sluk efter ugeplan', steps: [`Tryk "Tidsplan" på skærmen, ret tiderne, og tryk "Gem tidsplan".`] },
        { title: 'Ny version af TV-klienten', steps: [`Tryk "Opdater til ..." på skærmen, eller "Opdater N skærme, der er bagud".`] }
      ],
      tips: [
        `Koden fra TV'et virker i et kvarter. Genstart Pi'en for at få en ny.`,
        `En opdatering ruller selv tilbage, hvis den nye version ikke melder sig inden for 3 minutter.`
      ]
    },
    {
      id: 'dashboards',
      title: `Dashboards - det, TV'et viser`,
      icon: '📊',
      role: 'Administrator',
      where: `Administration → "Dashboards"`,
      intro: `Vælg hvilke sager TV'et viser, om de skal stå i en tabel eller på et kort, og om dagens bemanding skal stå ved siden af.`,
      sketch: {
        device: 'tv',
        title: 'PROJEKTOVERSIGT',
        caption: `"Forhåndsvis TV-visning" viser, hvordan det ser ud`,
        blocks: [
          { t: 'cols', left: [{ t: 'row', text: '16 aktive projekter' }], right: [{ t: 'row', text: '1 forsinket' }] },
          { t: 'row', text: 'SMS Gateway · Simon · Afventer', right: '14 %', n: 1 },
          { t: 'row', text: 'Tavle - Industrivej 12 · Pia · I gang', right: '30 %' },
          { t: 'row', text: 'Dagens bemanding: Jens · Mikkel · Anna', n: 2 },
          { t: 'text', text: 'Forhåndsvisning - sådan ser skærmen ud' }
        ]
      },
      flows: [
        {
          title: 'Ret dashboardet',
          steps: [
            n(1, `Tilføj filtre - team, projektleder, status eller kategori - med "Tilføj filter".`),
            n(2, `Sæt flueben i "Vis dagens bemanding", og vælg hvem der vises.`),
            `Sæt flueben i "Vis sagerne på et kort i stedet for tabellen", hvis skærmen skal vise, hvor sagerne ligger.`,
            `Tryk "Gem". Skærmene henter den nye opsætning.`,
            `Tryk "Forhåndsvis TV-visning" for at se resultatet i en ny fane.`
          ]
        }
      ],
      tips: [
        `Filtre af samme type kombineres med ELLER, forskellige typer med OG.`,
        `Rækkefølgen i tabellen: de startede sager med lavest fremdrift øverst, "Ikke startet" nederst og færdige til sidst.`,
        `Fremdriften er farvet: rød under 25 %, gul fra 25 % og grøn fra 90 %.`,
        `Er der flere sager, end skærmen har plads til, viser den selv så mange, der kan være, og "+ N flere sager" nederst - linjen i bunden bliver altid stående.`,
        `Kortet viser alle åbne sager, der passer til filtrene - "Maks. antal projekter" gælder kun tabellen. Flere sager på samme adresse står som én nål med "+2". Rød nål = deadline overskredet.`,
        `Mangler en sag på kortet, har hverken sagen eller kunden en adresse, der kunne slås op. Tallet står nederst på kortet.`
      ]
    },
    {
      id: 'activity-admin',
      title: 'Hele aktivitetsloggen',
      icon: '🧾',
      role: 'Administrator',
      where: `Administration → "Aktivitetslog"`,
      intro: `Alt, der er sket i hele systemet. Loggen for én sag findes på sagen.`,
      flows: [{ title: 'Find noget', steps: [`Vælg type, og blad med "Forrige" og "Næste".`] }]
    },
    {
      id: 'modules-operations',
      title: 'Moduler og drift',
      icon: '🧩',
      role: 'Administrator',
      where: `Administration → "Moduler" og "Drift"`,
      intro: `Slå de dele af systemet fra, huset ikke bruger - de forsvinder fra menuen og fra vejledningen. Under "Drift" ser du, om natten sikkerhedskopi er taget.`,
      flows: [
        { title: 'Slå et modul fra', steps: [`Tryk "Slå fra" ud for modulet. Data slettes ikke.`] },
        {
          title: 'Slå en funktion på intranettet fra (Superadmin)',
          steps: [
            `Under "Intranet" står intranettets funktioner - afstemninger, idéboks, vagtplan og de andre.`,
            `Tryk "Slå fra" ud for funktionen. Fanen, punktet i "+"-menuen og afsnittet i vejledningen forsvinder.`,
            `Slår du hele Intranet fra, er alle funktionerne fra - men de står, som du satte dem, når det tændes igen.`
          ]
        },
        { title: 'Tjek sikkerhedskopien', steps: [`Åbn "Drift", og se, at kopien er fra i nat.`] }
      ],
      tips: [
        `Kopien tages hver nat kl. 02.30 og gemmes i 30 dage.`,
        `Kun Superadmin kan tænde og slukke for intranettets funktioner. Nyheder, arbejdsgrupper og telefonlisten følger selve Intranet.`,
        `"Velkommen til nye" bygger på interne opgaver - slukkes de, er velkomsten også fra.`
      ]
    },
    {
      id: 'microsoft365',
      title: 'Microsoft 365',
      icon: '🔗',
      role: 'Superadmin',
      where: `Administration → "Microsoft 365"`,
      intro: `Forbindelsen til Microsoft 365: login, brugersynkronisering, filer i SharePoint og mail fra systemet.`,
      flows: [
        { title: 'Forbindelsen', steps: [`Udfyld tenant, klient-id og hemmelighed, tryk "Gem opsætning" og "Test forbindelse".`] },
        { title: 'Brugersynkronisering', steps: [`Sæt interval og standardrolle, vælg "Slået til", tryk "Gem indstillinger" og "Synkronisér nu".`] },
        { title: 'Mail', steps: [`Skriv afsender, sæt flueben i "Send mail fra systemet", tryk "Gem", og send en testmail.`] }
      ],
      tips: [
        `Brugere, der er fjernet i Microsoft 365, deaktiveres - de slettes aldrig.`,
        `Afsenderen skal være en rigtig postkasse.`
      ]
    },
    {
      id: 'sms',
      title: 'SMS-gateway',
      icon: '💬',
      role: 'Superadmin',
      where: `Administration → "SMS"`,
      intro: `Adressen og nøglen til SMS-gatewayen.`,
      flows: [
        { title: 'Sæt op', steps: [`Skriv adresse og API-nøgle, sæt flueben i "Send SMS fra systemet", og tryk "Gem opsætning".`] },
        { title: 'Prøv af', steps: [`Skriv et nummer og en besked, og tryk "Send test-SMS".`] }
      ],
      tips: [`Et emoji halverer pladsen fra 160 til 70 tegn. En test koster som en rigtig SMS.`]
    },
    {
      id: 'ordrestyring-api',
      title: 'Ordrestyrings API',
      icon: '🧾',
      role: 'Superadmin',
      where: `Administration → "Ordrestyring"`,
      intro: `Nøglen, der giver Overblik adgang til jeres data i Ordrestyring. Den fås hos Ordrestyring (api@ordrestyring.dk).`,
      flows: [
        { title: 'Sæt op', steps: [`Indsæt nøglen, og tryk "Gem nøglen". Den prøves med det samme, og svaret står under "Forbindelsen".`] },
        { title: 'Prøv og hent skemaet', steps: [`"Prøv forbindelsen" tjekker nøglen uden at ændre noget i Ordrestyring.`, `"Hent skemaet" giver en fil med alle Ordrestyrings kald og felter - læg den i projektets docs-mappe.`] }
      ],
      tips: [`Nøglen gemmes krypteret og kan ikke hentes frem igen. Indsæt den aldrig i en mail eller en chat.`]
    },
    {
      id: 'vpn-admin',
      title: 'VPN til kundeanlæg',
      icon: '🔐',
      role: 'Superadmin',
      module: 'vpn',
      where: `Administration → "VPN"`,
      intro: `Sites med en WireGuard-router hos kunderne, og tidsbegrænset serviceadgang for én montør til ét site. Al adgang er lukket, indtil du giver den - og den lukker af sig selv.`,
      sketch: {
        device: 'desktop',
        title: 'VPN til kundeanlæg',
        blocks: [
          {
            t: 'card',
            title: 'Serviceadgang',
            children: [
              { t: 'field', label: 'Site', value: 'Østparken - Batterianlæg 1 (Online)', n: 1 },
              { t: 'field', label: 'Montør', value: 'Jens Montør', n: 2 },
              { t: 'field', label: 'Varighed', value: '2 timer' },
              { t: 'field', label: 'Begrundelse', value: 'Fejlsøgning på inverter' },
              { t: 'btn', text: 'Godkend adgang', primary: true, n: 3 },
              { t: 'row', text: 'Jens Montør → Batterianlæg 1', meta: 'Fejlsøgning', right: 'Venter på montøren', n: 4 }
            ]
          },
          {
            t: 'card',
            title: 'Sites',
            children: [
              { t: 'btn', text: 'Opret site og lav routerfil', primary: true, n: 5 },
              { t: 'banner', text: 'Routerfil klar - kan kun hentes én gang', tone: 'warn' },
              { t: 'btn', text: 'Hent routerfil (tcvpn-site1.conf)', primary: true, n: 6 },
              { t: 'row', text: 'Batterianlæg 1 · Østparken', meta: '10.50.0.2', right: 'Online', n: 7 },
              { t: 'btns', items: [{ text: 'Ny routerfil' }, { text: 'Spær', danger: true }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Sæt et nyt anlæg op',
          steps: [
            n(5, `Tryk "Opret site", vælg kunde og evt. enhed, skriv et navn, og vælg evt. routerens model. Routeren oprettes af sig selv som enhed hos kunden, så den får QR-mærkat og historik. Vælger du EdgeRouter ER-6P, får CLI-filen også grundopsætningen: WAN på eth0 (DHCP), eth1-eth5 i broen br0 med routerens LAN-adresse, DHCP-server og masquerade - og LAN-interfacet bliver br0. Vælger du EdgeRouter X, er det det samme, men eth1-eth4 i switchen switch0. Er modellen ikke valgt, kommer grundopsætningen IKKE med - vælg den på routerens enhed under Enheder. Vælg "Routerens opsætning": tekstfil til EdgeRouter CLI, WireGuard-fil eller at routeren selv laver nøglen. Udfyld kundens LAN (påkrævet) - så kommer LAN-kommandoerne med i tekstfilen, og montøren kan arbejde på kundens net. Tryk "Opret site og lav routerfil". Nøglen laves i din browser.`),
            n(6, `Vælg filtypen: "Hent WireGuard-fil" (importeres i routerens WireGuard-opsætning) eller "Hent EdgeRouter CLI" (kommandoer til en Ubiquiti EdgeRouter - log ind med SSH og indsæt). Der kan kun hentes ÉN fil, én gang - slet den fra computeren bagefter. Indsæt den over SSH, og tryk derefter "Tjek opsætningen" på sitet.`),
            n(7, `Status skifter til "Online", når routeren har forbundet.`)
          ]
        },
        {
          title: 'Giv en montør adgang',
          steps: [
            n(1, `Vælg sitet. Kun sites med routernøgle kan vælges.`),
            n(2, `Vælg montøren. Montøren skal selv have lavet sin VPN-nøgle under "VPN-adgang".`),
            n(3, `Vælg varighed, skriv hvorfor, og tryk "Godkend adgang". Er "Kræv YubiKey" slået til, bekræfter du med din YubiKey.`),
            n(4, `Godkendelsen står som "Venter på montøren". Montøren åbner den selv under "VPN-adgang" inden 24 timer - først da løber tiden. Tryk "Annullér" eller "Luk nu" for at stoppe den; også åbne forbindelser stoppes.`)
          ]
        },
        {
          title: 'Montøren skal kunne arbejde på kundens LAN',
          steps: [
            `Tryk "LAN-adgang og EdgeRouter" på sitet.`,
            `Skriv kundens LAN (altid et /24, fx 10.122.1.0/24). Routerens LAN-adresse er .240, hvis feltet er tomt - ret den, hvis routeren står på en anden adresse. Det SKAL være routerens primære adresse på det interface; en ekstra adresse virker ikke. Montøren optræder som netop den over for kundens udstyr. Routerens LAN-interface er den port, der sidder på kundens net (fx eth1 eller br0).`,
            `Tryk "Gem LAN-adgang" og bekræft med din YubiKey, hvis den kræves.`,
            `Kopiér EdgeRouter-opsætningen og indsæt kommandoerne på routeren over SSH (fx PuTTY). Brug IKKE CLI-vinduet i routerens web-UI - det taber linjeskiftene, så kommandoerne limes sammen til én linje, og intet bliver udført. Kommandoerne rører ikke tunnelen - den er sat op med routerfilen.`,
            `Tryk til sidst "Tjek opsætningen". Gatewayen pinger sitets alias på montørens adresse, som ligger på routeren selv - svarer den, er både oversættelsen og montøradressen på plads. Svarer den ikke, er kommandoerne ikke kommet ind.`,
            `Kolonnen "Kundens LAN" viser, hvilket net montøren skriver (fx 10.52.0.0/24), og om opsætningen er bekræftet. Kun én montør kan have et site ad gangen - "I brug af" viser hvem.`
          ]
        },
        {
          title: 'Se hvilke sites der mangler opsætning',
          steps: [
            `Øverst i "Sites" står der gult, hvis et site mangler kundens LAN - montøren kan så kun nå routeren selv, ikke kundens udstyr.`,
            `Står et site som "Ikke bekræftet", er LAN'et udfyldt her, men gatewayen har aldrig fået svar fra routeren. Tryk "Tjek opsætningen" på sitet.`,
            `"Routeren svarede ikke" betyder, at kommandoerne ikke er inde, at routeren er offline, eller at montøradressen er optaget af andet udstyr hos kunden.`
          ]
        },
        {
          title: 'Print en label til routeren',
          steps: [
            `Tryk "Print label" under sitets navn.`,
            `Labelen viser sitets navn, kunden, VPN-IP'en, LAN via VPN (det, montøren skriver), kundens LAN og routerens LAN-adresse. Ingen nøgler.`,
            `Vælg "Brother QL, 62 mm endeløs", tryk "Print", og vælg papirstørrelse 62 mm x 40 mm, margener "Ingen" og skalering 100 % i printvinduet.`
          ]
        },
        {
          title: 'Routeren på et site fra før',
          steps: [`Står der "Opret routeren som enhed" under sitets navn, har routeren ingen enhed endnu. Tryk på den - så står routeren hos kunden under Enheder. Den kræver ikke YubiKey, for den åbner ingenting.`]
        },
        {
          title: 'Ret sitets adresse',
          steps: [`Tryk "Ret adresse" ved sitets navn, skriv adresse, postnr. og by for der, hvor routeren sidder, og tryk "Gem adresse". Den rettes også på VPN-gatewayen, og routerens enhed under Enheder får både navnet og adressen - så den står rigtigt på kortet. Den kræver ikke YubiKey, for den åbner ingenting.`]
        },
        { title: 'Routerfilen er væk, eller routeren er skiftet', steps: [`Tryk "Ny routerfil" på sitet og hent den nye fil. Den gamle fil holder op med at virke med det samme.`] },
        { title: 'Mistet telefon', steps: [`Under "Montørernes VPN-profiler": tryk "Spær profil". Al adgang lukkes med det samme.`] }
      ],
      tips: [
        `Kun Superadmin kan godkende adgang. Adgangen er højst 8 timer og gælder kun det ene site.`,
        `"Kræv YubiKey" (nederst på siden) kræver sikkerhedsnøgle for at oprette site, lave routerfil, godkende adgang - og for montøren at lave nøgle og åbne adgang. At lukke, spærre og annullere kræver det aldrig. Opsætningen i Microsoft 365 står i INSTALL.md afsnit 5j.`,
        `Genstarter VPN-gatewayen, lukkes al adgang - giv den igen, hvis arbejdet ikke er færdigt.`,
        `Routerfilen indeholder routerens private nøgle. Den gemmes ingen steder og kan derfor kun hentes én gang.`,
        `Laver routeren selv sin nøgle: sæt flueben ved "Routeren laver selv sin nøgle" og brug "Routerens egen nøgle" bagefter.`,
        `Modulet "VPN til kundeanlæg" skal være tændt under Moduler.`
      ]
    }
  ]
}
